// HEIC-Decoder wird dynamisch geladen, damit das WASM nicht in den Haupt-Bundle wandert.
type HeifDecoder = {
  decode: (data: Uint8Array) => Array<{
    get_width(): number;
    get_height(): number;
    display(
      image: { data: Uint8ClampedArray; width: number; height: number },
      cb: (img: { data: Uint8ClampedArray; width: number; height: number }) => void,
    ): void;
  }>;
};

let heifModule: HeifDecoder | null = null;
async function loadHeif(): Promise<HeifDecoder> {
  if (heifModule) return heifModule;
  // @ts-expect-error – libheif-js liefert kein TS-Typing mit
  const mod = await import("libheif-js");
  const HeifDecoderCtor = mod.HeifDecoder ?? mod.default?.HeifDecoder;
  heifModule = new HeifDecoderCtor();
  return heifModule!;
}

export type DecodedImage = ImageData;

export async function decodeImage(fileName: string, data: ArrayBuffer): Promise<DecodedImage> {
  if (/\.(heic|heif)$/i.test(fileName)) {
    return decodeHeic(data);
  }
  return decodeJpeg(data);
}

async function decodeJpeg(data: ArrayBuffer): Promise<ImageData> {
  // Ohne festen Typ – createImageBitmap erkennt JPG/PNG/WebP selbst.
  const blob = new Blob([data]);
  const bitmap = await createImageBitmap(blob);
  const canvas = new OffscreenCanvas(bitmap.width, bitmap.height);
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Konnte 2D-Kontext nicht erzeugen");
  ctx.drawImage(bitmap, 0, 0);
  bitmap.close();
  return ctx.getImageData(0, 0, canvas.width, canvas.height);
}

async function decodeHeic(data: ArrayBuffer): Promise<ImageData> {
  const decoder = await loadHeif();
  const images = decoder.decode(new Uint8Array(data));
  if (images.length === 0) throw new Error("HEIC enthält kein dekodierbares Bild");
  const img = images[0];
  const width = img.get_width();
  const height = img.get_height();
  const target = { data: new Uint8ClampedArray(width * height * 4), width, height };
  return await new Promise<ImageData>((resolve) => {
    img.display(target, (result) => {
      // Kopie in einen detachten ArrayBuffer-backed Uint8ClampedArray
      const copy = new Uint8ClampedArray(result.data.length);
      copy.set(result.data);
      resolve(new ImageData(copy, result.width, result.height));
    });
  });
}
